import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { ProtectedRoute } from "@/components/layout/ProtectedRoute";
import { Layout } from "@/components/layout/Layout";
import { LoginPage } from "@/pages/auth/LoginPage";
import { RegisterPage } from "@/pages/auth/RegisterPage";
import { ResetPasswordPage } from "@/pages/auth/ResetPasswordPage";
import { HomePage } from "@/pages/HomePage";
import { VowsPage } from "@/pages/VowsPage";
import { LearnPage } from "@/pages/LearnPage";
import { DailyOverviewPage } from "@/pages/DailyOverviewPage";
import { ProfilePage } from "@/pages/ProfilePage";
import { SettingsPage } from "@/pages/SettingsPage";
import { TimeslotSettingsPage } from "@/pages/TimeslotSettingsPage";
import { MeditationTimerPage } from "@/pages/MeditationTimerPage";
import { JournalHistoryPage } from "@/pages/JournalHistoryPage";
import { LegalPage } from "@/pages/LegalPage";
import { CheckInFlow } from "@/components/CheckInFlow";
import { EntryForm } from "@/components/journal/EntryForm";
import { useEffect } from "react";
import { useTheme } from "@/hooks/useTheme";
import { useAuth } from "@/hooks/useAuth";
import { loadSettingsFromSupabase } from "@/lib/settingsSync";

function ThemeInit() {
  // Activate the theme hook so it manages the dark class
  useTheme();
  return null;
}

function SettingsLoader() {
  const { user } = useAuth();
  useEffect(() => {
    if (!user?.id) return;
    loadSettingsFromSupabase(user.id).then(() => {
      window.dispatchEvent(new Event("vajra-settings-loaded"));
    });
  }, [user?.id]);
  return null;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ThemeInit />
        <SettingsLoader />
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/legal" element={<LegalPage />} />

          {/* Protected full-screen routes (no layout shell) */}
          <Route
            path="/checkin"
            element={
              <ProtectedRoute>
                <CheckInFlow />
              </ProtectedRoute>
            }
          />
          <Route
            path="/entry"
            element={
              <ProtectedRoute>
                <EntryForm />
              </ProtectedRoute>
            }
          />

          {/* Protected routes with layout */}
          <Route
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route path="/" element={<HomePage />} />
            <Route path="/vows" element={<VowsPage />} />
            <Route path="/learn" element={<LearnPage />} />
            <Route path="/overview" element={<DailyOverviewPage />} />
            <Route path="/stats" element={<DailyOverviewPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/settings/timeslots" element={<TimeslotSettingsPage />} />
            <Route path="/meditation" element={<MeditationTimerPage />} />
            <Route path="/journal" element={<JournalHistoryPage />} />
          </Route>
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
